'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth';
import type { DailyClaimResult } from '@/lib/api';
import MarqCoin from '../MarqCoin';
import styles from './Rewards.module.css';

export default function RewardCard({ compact = false }: { compact?: boolean }) {
  const { marq, claimDaily, refreshWallet } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DailyClaimResult | null>(null);
  const [error, setError] = useState('');
  const [remaining, setRemaining] = useState<number | null>(null);
  const daily = marq?.daily;
  const reset = daily?.resetAt ?? marq?.nextDailyAt;
  useEffect(() => {
    if (!reset) return;
    const offset = daily?.serverNow ? Date.parse(daily.serverNow) - Date.now() : 0;
    let lastRefresh = 0;
    const tick = () => {
      const ms = Math.max(0, Date.parse(reset) - Date.now() - offset);
      setRemaining(ms);
      if (ms <= 0 && Date.now() - lastRefresh >= 15_000) { lastRefresh = Date.now(); void refreshWallet().catch(() => undefined); }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [reset, daily?.serverNow, refreshWallet]);
  useEffect(() => { setResult(null); setError(''); }, [daily?.day]);
  if (!marq) return null;
  const count = daily?.cycle ?? 0;
  const day = daily?.todayDay ?? 1;
  const firstShown = Math.floor((day - 1) / 7) * 7 + 1;
  const shownDays = Math.min(7, count - firstShown + 1);
  const available = marq.canClaimDaily;
  const shownBase = available ? daily?.base : daily?.lastBase;
  const shownBonus = available ? (day === count ? daily?.bonus : 0) : daily?.lastBonus;
  const claim = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try { setResult(await claimDaily()); }
    catch (e) { setError(e instanceof Error ? e.message : 'Your reward could not be confirmed. Please try again.'); }
    finally { setBusy(false); }
  };
  const hours = Math.floor((remaining ?? 0) / 3600_000);
  const minutes = Math.floor(((remaining ?? 0) % 3600_000) / 60_000);
  return <section className={`${styles.dailyCard} ${compact ? styles.compact : ''}`} aria-label="Daily KHARMA reward">
    <div className={styles.cardTop}>
      <span className={styles.eyebrow}>A LITTLE THANK YOU, EVERY DAY</span>
      <span className={`${styles.badge} ${available ? styles.readyBadge : ''}`}><span aria-hidden>{available ? '●' : '✓'}</span> {available ? 'Ready to collect' : 'Collected today'}</span>
    </div>
    <div className={styles.rewardHeadline}>
      <div><h2>{available ? 'Your daily reward' : 'Today, well earned.'}</h2>
        <p>{available ? 'Drop in. Collect your KHARMA. Keep your streak going.' : 'You’re all set. Come back tomorrow to keep it going.'}</p></div>
      <MarqCoin className={styles.heroCoin} />
    </div>
    <div className={styles.amount}><span>{available || daily?.lastAmount != null ? `+${(available ? marq.dailyAmount : daily!.lastAmount!).toLocaleString('en-US')}` : 'Collected'}</span>{(available || daily?.lastAmount != null) && <span>KHARMA</span>}</div>
    {daily && <p className={styles.breakdown}>{shownBase != null ? `${shownBase.toLocaleString()} daily reward${shownBonus ? ` + ${shownBonus.toLocaleString()} streak bonus` : ''}` : 'Your reward was collected earlier today.'}</p>}
    {daily && <>
      <div className={styles.streakHeading}><strong>{daily.streak} day{daily.streak === 1 ? '' : 's'} in a row</strong><span>Best: {daily.bestStreak} days</span></div>
      {count > 7 && <p className={styles.streakNote}>Days {firstShown}–{firstShown + shownDays - 1} of {count}</p>}
      <ol className={styles.days} style={{ '--days': shownDays } as React.CSSProperties} aria-label={`${count}-day reward cycle`}>
        {Array.from({ length: shownDays }, (_, index) => {
          const n = firstShown + index, done = n < day || (n === day && !available), current = n === day && available;
          const value = daily.base + (n === count ? daily.bonus : 0);
          return <li key={n} className={`${styles.day} ${done ? styles.dayDone : ''} ${current ? styles.dayCurrent : ''} ${n === count && daily.bonus > 0 ? styles.dayBonus : ''}`} aria-current={current ? 'step' : undefined}>
            <span>Day {n}</span><span className={styles.dayIcon} aria-hidden>{done ? '✓' : n === count && daily.bonus > 0 ? '✦' : '◈'}</span><strong>+{value.toLocaleString()}</strong>
            <span className="sr-only"> KHARMA{done ? ', collected' : current ? ', available today' : ', upcoming'}</span>
          </li>;
        })}
      </ol>
      <p className={styles.streakNote}>{daily.bonus > 0 ? <><strong>+{daily.bonus.toLocaleString()} extra</strong> every {count} consecutive days. </> : null}Missing a day starts a new streak; your KHARMA stays yours.</p>
    </>}
    <button type="button" className={styles.claimButton} onClick={claim} disabled={busy || !available} aria-busy={busy}>
      {busy ? 'Collecting your reward…' : available ? <>Collect {marq.dailyAmount.toLocaleString()} KHARMA <span aria-hidden>↗</span></> : <>✓ Collected for today</>}
    </button>
    <div role="status" aria-live="polite" className={result ? styles.success : styles.statusSpace}>
      {result?.claimed ? `+${result.amount.toLocaleString()} KHARMA added${result.bonus ? `, including your ${result.bonus.toLocaleString()} streak bonus` : ''}.` : result ? 'Already collected today. Your balance is up to date.' : null}
    </div>
    {error && <div role="alert" className={styles.error}>{error} <button type="button" onClick={() => void refreshWallet().catch(() => setError('Your wallet is still unavailable. Please try again shortly.'))}>Refresh wallet</button></div>}
    <div className={styles.resetLine}><span aria-hidden>◷</span><span>{remaining === null ? 'Checking reset time…' : remaining <= 0 ? 'Refreshing today’s reward…' : `Resets in ${hours}h ${String(minutes).padStart(2, '0')}m`}</span><span title="Rewards reset at midnight UTC for everyone">00:00 UTC</span></div>
    {compact && <Link href="/rewards" className={styles.allRewards}>Your rewards & activity <span aria-hidden>→</span></Link>}
  </section>;
}
